import { Plus, X } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Button, Checkbox, Input, RichTextEditor, Select } from '@/components/ui';
import {
  COLLECTION_FIELD_TYPES,
  emptyConsent,
  emptyField,
  fieldTypeUsesOptions,
  type CollectionField,
  type CollectionFieldType,
  type ConsentItem,
  type ProductCollectionConfig,
} from './types';

export interface ProductCollectionEditorProps {
  value: ProductCollectionConfig;
  disabled?: boolean;
  onChange: (next: ProductCollectionConfig) => void;
}

const FIELD_TYPE_LABELS: Record<CollectionFieldType, string> = {
  TEXT: '단답형',
  TEXTAREA: '장문형',
  RADIO: '라디오',
  CHECKBOX: '체크박스',
  SELECT: '드롭다운',
};

/** ID41 상품 커스텀 폼 빌더 + 가변 동의 항목 에디터 (controlled). */
export const ProductCollectionEditor = ({ value, disabled, onChange }: ProductCollectionEditorProps) => {
  const { t } = useTranslation('products');

  const updateField = (idx: number, patch: Partial<CollectionField>) =>
    onChange({
      ...value,
      fields: value.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    });

  const removeField = (idx: number) =>
    onChange({ ...value, fields: value.fields.filter((_, i) => i !== idx) });

  const addField = () => onChange({ ...value, fields: [...value.fields, emptyField()] });

  const updateConsent = (idx: number, patch: Partial<ConsentItem>) =>
    onChange({
      ...value,
      consents: value.consents.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    });

  const removeConsent = (idx: number) =>
    onChange({ ...value, consents: value.consents.filter((_, i) => i !== idx) });

  const addConsent = () => onChange({ ...value, consents: [...value.consents, emptyConsent()] });

  return (
    <div className="flex flex-col gap-8">
      {/* 1) 커스텀 입력 필드 빌더 */}
      <div className="flex flex-col gap-3">
        <h4 className="text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>
          {t('products.collection.fieldsTitle', '추가 입력 필드')}
        </h4>
        {value.fields.length === 0 ? (
          <div
            className="flex items-center justify-center rounded-md py-6 text-[13px]"
            style={{ background: 'var(--surface-0)', border: '1px dashed var(--divider-strong)', color: 'var(--content-muted)' }}
          >
            {t('products.collection.fieldsEmpty', '추가된 입력 필드가 없습니다.')}
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {value.fields.map((field, idx) => (
              <li
                key={field.key}
                className="flex flex-col gap-3 rounded-md p-4"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_180px_auto]">
                  <Input
                    type="text"
                    value={field.label}
                    disabled={disabled}
                    maxLength={100}
                    placeholder={t('products.collection.fieldLabelPlaceholder', '필드 라벨 (예: 응모자 닉네임)')}
                    aria-label={t('products.collection.fieldLabel', '필드 라벨')}
                    onChange={(e) => updateField(idx, { label: e.target.value })}
                  />
                  <Select
                    value={field.type}
                    disabled={disabled}
                    aria-label={t('products.collection.fieldType', '필드 유형')}
                    onChange={(e) => {
                      const type = e.target.value as CollectionFieldType;
                      updateField(idx, {
                        type,
                        options: fieldTypeUsesOptions(type) ? (field.options.length ? field.options : ['']) : [],
                      });
                    }}
                  >
                    {COLLECTION_FIELD_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {FIELD_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => removeField(idx)}
                    aria-label={t('products.collection.removeField', '필드 삭제')}
                  >
                    <X size={14} style={{ color: 'var(--signal-danger)' }} />
                  </Button>
                </div>

                {/* 선택형 옵션 편집 */}
                {fieldTypeUsesOptions(field.type) ? (
                  <div className="flex flex-col gap-2 pl-1">
                    <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                      {t('products.collection.options', '선택 항목')}
                    </span>
                    {field.options.map((opt, optIdx) => (
                      <div key={`${field.key}-opt-${optIdx}`} className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={opt}
                          disabled={disabled}
                          maxLength={100}
                          placeholder={t('products.collection.optionPlaceholder', '선택 항목')}
                          aria-label={t('products.collection.optionAria', { index: optIdx + 1, defaultValue: `선택 항목 ${optIdx + 1}` })}
                          onChange={(e) =>
                            updateField(idx, {
                              options: field.options.map((o, i) => (i === optIdx ? e.target.value : o)),
                            })
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={disabled || field.options.length <= 1}
                          onClick={() => updateField(idx, { options: field.options.filter((_, i) => i !== optIdx) })}
                          aria-label={t('products.collection.removeOption', '항목 삭제')}
                        >
                          <X size={13} style={{ color: 'var(--content-muted)' }} />
                        </Button>
                      </div>
                    ))}
                    <div>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={disabled}
                        onClick={() => updateField(idx, { options: [...field.options, ''] })}
                      >
                        <Plus size={13} />
                        {t('products.collection.addOption', '항목 추가')}
                      </Button>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-5">
                  <label className="inline-flex cursor-pointer items-center gap-2 text-[13px]" style={{ color: 'var(--content-default)' }}>
                    <Checkbox
                      checked={field.required}
                      disabled={disabled}
                      onCheckedChange={(next) => updateField(idx, { required: next === true })}
                      aria-label={t('products.collection.required', '필수 입력')}
                    />
                    {t('products.collection.required', '필수 입력')}
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2 text-[13px]" style={{ color: 'var(--content-default)' }}>
                    <Checkbox
                      checked={field.sensitive}
                      disabled={disabled}
                      onCheckedChange={(next) => updateField(idx, { sensitive: next === true })}
                      aria-label={t('products.collection.sensitive', '개인정보 (동의 필요)')}
                    />
                    {t('products.collection.sensitive', '개인정보 (동의 필요)')}
                  </label>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div>
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={addField}>
            <Plus size={14} />
            {t('products.collection.addField', '입력 필드 추가')}
          </Button>
        </div>
      </div>

      {/* 2) 가변 동의 항목 */}
      <div className="flex flex-col gap-3 border-t pt-6" style={{ borderColor: 'var(--divider-soft)' }}>
        <h4 className="text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>
          {t('products.collection.consentsTitle', '개별 동의 항목')}
        </h4>
        {value.consents.length === 0 ? (
          <div
            className="flex items-center justify-center rounded-md py-6 text-[13px]"
            style={{ background: 'var(--surface-0)', border: '1px dashed var(--divider-strong)', color: 'var(--content-muted)' }}
          >
            {t('products.collection.consentsEmpty', '추가된 동의 항목이 없습니다.')}
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {value.consents.map((consent, idx) => (
              <li
                key={consent.key}
                className="flex flex-col gap-3 rounded-md p-4"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
                  <Input
                    type="text"
                    value={consent.title}
                    disabled={disabled}
                    maxLength={150}
                    placeholder={t('products.collection.consentTitlePlaceholder', '동의 항목 타이틀 (예: 개인정보 수집·이용 동의)')}
                    aria-label={t('products.collection.consentTitle', '동의 타이틀')}
                    onChange={(e) => updateConsent(idx, { title: e.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => removeConsent(idx)}
                    aria-label={t('products.collection.removeConsent', '동의 항목 삭제')}
                  >
                    <X size={14} style={{ color: 'var(--signal-danger)' }} />
                  </Button>
                </div>
                {/* R4-A ⑤ — 약관 본문은 contentHtml(HTML). SF PDP 는 SafeHtml sanitize 렌더 →
                    이벤트 동의 에디터와 동일하게 RichTextEditor(색/볼드/표/링크)로 상세 약관 작성. */}
                <div className="flex flex-col gap-1">
                  <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                    {t('products.collection.consentContent', '약관 본문')}{' '}
                    {t('products.collection.consentContentOptional', '(선택 · 링크만 사용 시 비움)')}
                  </span>
                  <RichTextEditor
                    value={consent.content}
                    onChange={(html) => updateConsent(idx, { content: html })}
                    minHeight={140}
                    aria-label={t('products.collection.consentContent', '약관 본문')}
                  />
                </div>
                <Input
                  type="url"
                  value={consent.linkUrl}
                  disabled={disabled}
                  maxLength={500}
                  placeholder={t('products.collection.consentLinkPlaceholder', '외부 약관 링크 (선택) https://…')}
                  aria-label={t('products.collection.consentLink', '약관 링크')}
                  onChange={(e) => updateConsent(idx, { linkUrl: e.target.value })}
                />
                <label className="inline-flex cursor-pointer items-center gap-2 text-[13px]" style={{ color: 'var(--content-default)' }}>
                  <Checkbox
                    checked={consent.required}
                    disabled={disabled}
                    onCheckedChange={(next) => updateConsent(idx, { required: next === true })}
                    aria-label={t('products.collection.consentRequired', '필수 동의')}
                  />
                  {t('products.collection.consentRequired', '필수 동의')}
                </label>
              </li>
            ))}
          </ul>
        )}
        <div>
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={addConsent}>
            <Plus size={14} />
            {t('products.collection.addConsent', '동의 항목 추가')}
          </Button>
        </div>
      </div>
    </div>
  );
};
